import { HashiBridgeKey } from '../interfaces/hashi-bridge-key';
import { HashiIsland } from '../interfaces/hashi-island';

export type HashiBridgesMap = Record<string, number>;

export interface HashiPoint {
  x: number;
  y: number;
}

export interface HashiSegment {
  type: 'h' | 'v';
  x?: number;
  y?: number;
  x1?: number;
  x2?: number;
  y1?: number;
  y2?: number;
}

const RAYCAST_MAX_STEPS = 1001;

export function sortKey(k: HashiBridgeKey): HashiBridgeKey {
  return k.a < k.b ? k : { a: k.b, b: k.a };
}

export function serializeKey(k: HashiBridgeKey): string {
  return k.a < k.b ? `${k.a}|${k.b}` : `${k.b}|${k.a}`;
}

export function buildIslandIndex(islands: HashiIsland[]): Record<string, HashiIsland> {
  const out: Record<string, HashiIsland> = {};
  for (const n of islands) out[n.id] = n;
  return out;
}

export function raycast(
  islands: HashiIsland[],
  from: HashiIsland,
  dx: number,
  dy: number
): HashiIsland | null {
  if (dx === 0 && dy === 0) return null;
  let x = from.x + dx;
  let y = from.y + dy;
  for (let i = 0; i < RAYCAST_MAX_STEPS; i++) {
    const hit = islands.find((m) => m.x === x && m.y === y);
    if (hit) return hit;
    x += dx;
    y += dy;
    if (x < -1000 || y < -1000 || x > 1000 || y > 1000) return null;
  }
  return null;
}

export function computeNeighborPairs(islands: HashiIsland[]): HashiBridgeKey[] {
  const seen = new Set<string>();
  const result: HashiBridgeKey[] = [];
  for (const n of islands) {
    const horiz = raycast(islands, n, 1, 0);
    const vert = raycast(islands, n, 0, 1);
    for (const target of [horiz, vert]) {
      if (!target) continue;
      const k = sortKey({ a: n.id, b: target.id });
      const s = serializeKey(k);
      if (!seen.has(s)) {
        seen.add(s);
        result.push(k);
      }
    }
  }
  return result;
}

export function computeNeighborPairsFor(islands: HashiIsland[], n: HashiIsland): HashiBridgeKey[] {
  const seen = new Set<string>();
  const out: HashiBridgeKey[] = [];
  for (const [dx, dy] of [
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
  ] as Array<[number, number]>) {
    const target = raycast(islands, n, dx, dy);
    if (!target) continue;
    const k = sortKey({ a: n.id, b: target.id });
    const s = serializeKey(k);
    if (!seen.has(s)) {
      seen.add(s);
      out.push(k);
    }
  }
  return out;
}

export function isVisiblePair(islands: HashiIsland[], k: HashiBridgeKey): boolean {
  const idx = buildIslandIndex(islands);
  const a = idx[k.a];
  const b = idx[k.b];
  if (!a || !b) return false;
  if (a.x === b.x) {
    const minY = Math.min(a.y, b.y);
    const maxY = Math.max(a.y, b.y);
    return !islands.some((n) => n.x === a.x && n.y > minY && n.y < maxY);
  }
  if (a.y === b.y) {
    const minX = Math.min(a.x, b.x);
    const maxX = Math.max(a.x, b.x);
    return !islands.some((n) => n.y === a.y && n.x > minX && n.x < maxX);
  }
  return false;
}

export function normalizeKey(islands: HashiIsland[], key: HashiBridgeKey): HashiBridgeKey | null {
  const idx = buildIslandIndex(islands);
  const a = idx[key.a];
  const b = idx[key.b];
  if (!a || !b) return null;
  if (!(a.x === b.x || a.y === b.y)) return null;
  const neighbors = computeNeighborPairsFor(islands, a);
  const found = neighbors.find(
    (p) => (p.a === a.id && p.b === b.id) || (p.a === b.id && p.b === a.id)
  );
  if (!found) return null;
  return sortKey({ a: a.id, b: b.id });
}

function segmentOf(islands: HashiIsland[], k: HashiBridgeKey): HashiSegment {
  const idx = buildIslandIndex(islands);
  const a = idx[k.a];
  const b = idx[k.b];
  if (a.x === b.x) {
    return { type: 'v', x: a.x, y1: Math.min(a.y, b.y), y2: Math.max(a.y, b.y) };
  }
  return { type: 'h', y: a.y, x1: Math.min(a.x, b.x), x2: Math.max(a.x, b.x) };
}

function segmentsCross(a: HashiSegment, b: HashiSegment): boolean {
  if (a.type === 'h' && b.type === 'v') {
    return b.x! > a.x1! && b.x! < a.x2! && a.y! > b.y1! && a.y! < b.y2!;
  }
  if (a.type === 'v' && b.type === 'h') {
    return a.x! > b.x1! && a.x! < b.x2! && b.y! > a.y1! && b.y! < a.y2!;
  }
  return false;
}

export function createsCrossing(
  islands: HashiIsland[],
  bridgeKeys: HashiBridgeKey[],
  bridges: HashiBridgesMap,
  key: HashiBridgeKey,
  nextCount: number
): boolean {
  if (nextCount <= 0) return false;
  const seg = segmentOf(islands, key);
  const targetKeyS = serializeKey(key);
  for (const otherKey of bridgeKeys) {
    const sk = serializeKey(otherKey);
    if (sk === targetKeyS) continue;
    const cnt = bridges[sk] ?? 0;
    if (cnt === 0) continue;
    if (segmentsCross(seg, segmentOf(islands, otherKey))) return true;
  }
  return false;
}

export interface LineEndpoints {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export function lineEndpointsH(
  k: HashiBridgeKey,
  islands: HashiIsland[],
  cellSize: number,
  boardPadding: number
): LineEndpoints {
  const idx = buildIslandIndex(islands);
  const a = idx[k.a];
  const b = idx[k.b];
  const y = boardPadding + a.y * cellSize;
  const ax = boardPadding + a.x * cellSize;
  const bx = boardPadding + b.x * cellSize;
  return { x1: Math.min(ax, bx), y1: y, x2: Math.max(ax, bx), y2: y };
}

export function lineEndpointsV(
  k: HashiBridgeKey,
  islands: HashiIsland[],
  cellSize: number,
  boardPadding: number
): LineEndpoints {
  const idx = buildIslandIndex(islands);
  const a = idx[k.a];
  const b = idx[k.b];
  const x = boardPadding + a.x * cellSize;
  const ay = boardPadding + a.y * cellSize;
  const by = boardPadding + b.y * cellSize;
  return { x1: x, y1: Math.min(ay, by), x2: x, y2: Math.max(ay, by) };
}

export function getOrientation(islands: HashiIsland[], k: HashiBridgeKey): 'h' | 'v' | null {
  const idx = buildIslandIndex(islands);
  const a = idx[k.a];
  const b = idx[k.b];
  if (!a || !b) return null;
  if (a.y === b.y) return 'h';
  if (a.x === b.x) return 'v';
  return null;
}

export function currentDegree(bridges: HashiBridgesMap, islandId: string): number {
  let sum = 0;
  for (const sk of Object.keys(bridges)) {
    const cnt = bridges[sk] ?? 0;
    if (cnt === 0) continue;
    const [a, b] = sk.split('|');
    if (a === islandId || b === islandId) sum += cnt;
  }
  return sum;
}

export function isConnected(islands: HashiIsland[], bridges: HashiBridgesMap): boolean {
  if (islands.length === 0) return false;
  const adj: Record<string, string[]> = {};
  islands.forEach((n) => (adj[n.id] = []));
  for (const sk of Object.keys(bridges)) {
    const cnt = bridges[sk] ?? 0;
    if (cnt <= 0) continue;
    const [a, b] = sk.split('|');
    if (adj[a] && adj[b]) {
      adj[a].push(b);
      adj[b].push(a);
    }
  }
  const start = islands[0].id;
  const seen = new Set<string>();
  const stack = [start];
  while (stack.length) {
    const cur = stack.pop()!;
    if (seen.has(cur)) continue;
    seen.add(cur);
    for (const nb of adj[cur]) if (!seen.has(nb)) stack.push(nb);
  }
  return seen.size === islands.length;
}

export function computeProgress(islands: HashiIsland[], bridges: HashiBridgesMap): number {
  if (islands.length === 0) return 0;
  let totalReq = 0;
  const degree: Record<string, number> = {};
  const effective: Record<string, number> = {};
  for (const n of islands) {
    totalReq += n.requiredBridges;
    degree[n.id] = 0;
    effective[n.id] = 0;
  }
  for (const sk of Object.keys(bridges)) {
    const cnt = bridges[sk] ?? 0;
    if (cnt <= 0) continue;
    const [a, b] = sk.split('|');
    if (degree[a] !== undefined) degree[a] += cnt;
    if (degree[b] !== undefined) degree[b] += cnt;
  }
  const valid: Record<string, boolean> = {};
  for (const n of islands) valid[n.id] = degree[n.id] <= n.requiredBridges;
  for (const sk of Object.keys(bridges)) {
    const cnt = bridges[sk] ?? 0;
    if (cnt <= 0) continue;
    const [a, b] = sk.split('|');
    if (valid[a] && valid[b]) {
      if (effective[a] !== undefined) effective[a] += cnt;
      if (effective[b] !== undefined) effective[b] += cnt;
    }
  }
  let totalHave = 0;
  for (const n of islands) totalHave += Math.min(effective[n.id], n.requiredBridges);
  return totalReq > 0 ? totalHave / totalReq : 0;
}

export function isSolved(islands: HashiIsland[], bridges: HashiBridgesMap): boolean {
  if (islands.length === 0) return false;
  for (const n of islands) {
    if (currentDegree(bridges, n.id) !== n.requiredBridges) return false;
  }
  if (!isConnected(islands, bridges)) return false;
  const bridgeKeys = computeNeighborPairs(islands);
  for (const key of bridgeKeys) {
    const cnt = bridges[serializeKey(key)] ?? 0;
    if (cnt === 0) continue;
    if (createsCrossing(islands, bridgeKeys, bridges, key, cnt)) return false;
  }
  return true;
}
